import React from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { brandText } from '@/utils/brand-typography'
import { getPasswordRuleStates } from '@/modules/onboardingV2/helpers/password-rules'

interface PasswordRuleChipsProps {
  password?: string
  // after a failed submit the rules not met yet turn red
  highlightUnmet?: boolean
  dataQsId: string
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    margin: '8px 0 0',
    padding: 0,
    listStyle: 'none',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: theme.borderRadiusPill,
    ...brandText(theme, 'small'),
    backgroundColor: theme.palette.brand.divider,
    color: theme.palette.brand.text.secondary,
    transition: 'background-color 0.2s, color 0.2s',
  },
  met: {
    backgroundColor: theme.palette.brand.selected,
    color: theme.palette.brand.text.primary,
  },
  unmet: {
    backgroundColor: theme.redBackground,
    color: theme.palette.brand.error,
  },
  // always shown, so a chip keeps its width when the rule is met and the row does not wrap anew
  checkIcon: {
    width: 16,
    height: 16,
    color: 'inherit',
  },
  checkIconMet: {
    color: theme.palette.brand.main,
  },
}))

// The same chips below every password field of the new design - one set of rules, see helpers/password-rules
const PasswordRuleChips = ({ password = '', highlightUnmet = false, dataQsId }: PasswordRuleChipsProps) => {
  const classes = useStyles()

  return (
    <ul className={classes.list} data-qs-id={dataQsId} aria-live='polite'>
      {getPasswordRuleStates(password).map(({ key, label, met }) => (
        <li
          key={key}
          className={clsx(classes.chip, met && classes.met, !met && highlightUnmet && classes.unmet)}
          data-qs-id={`${dataQsId}-${key}`}
          data-met={met}
        >
          <CheckRoundedIcon className={clsx(classes.checkIcon, met && classes.checkIconMet)} aria-hidden />
          {label}
        </li>
      ))}
    </ul>
  )
}

export default PasswordRuleChips
